// Define types for Strapi image formats
interface StrapiImageFormat {
  url: string;
  width: number;
  height: number;
  size: number;
  ext: string;
  mime: string;
}

interface StrapiImageData {
  url: string;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
  width?: number;
  height?: number;
}

/**
 * Utility function to get the best available image URL from Strapi image object
 * Prioritizes the largest available format: original > large > medium > small > thumbnail
 */
export const getBestImageUrl = (imageData: StrapiImageData | string | null | undefined): string | null => {
  if (!imageData) return null;
  
  // If it's a string, use the existing logic
  if (typeof imageData === 'string') {
    return getImageUrl(imageData);
  }
  
  // If it's an object, choose the best available format
  const formats = imageData.formats;
  
  // Debug logging for image selection
  console.log('🖼️ Image selection for:', imageData.url?.split('/').pop());
  console.log('🖼️ Available formats:', Object.keys(formats || {}));
  console.log('🖼️ Main URL is thumbnail?', imageData.url?.includes('thumbnail_'));
  
  // Priority order: original (main url) > large > medium > small > thumbnail
  if (imageData.url && !imageData.url.includes('thumbnail_')) {
    console.log('🖼️ Using original image:', `${imageData.width}x${imageData.height}px`);
    return getImageUrl(imageData.url);
  }
  
  if (formats) {
    if (formats.large?.url) {
      console.log('🖼️ Using large format:', `${formats.large.width}x${formats.large.height}px`);
      return getImageUrl(formats.large.url);
    }
    if (formats.medium?.url) {
      console.log('🖼️ Using medium format:', `${formats.medium.width}x${formats.medium.height}px`);
      return getImageUrl(formats.medium.url);
    }
    if (formats.small?.url) {
      console.log('🖼️ Using small format:', `${formats.small.width}x${formats.small.height}px`);
      return getImageUrl(formats.small.url);
    }
    if (formats.thumbnail?.url) {
      console.log('⚠️ Only thumbnail available:', `${formats.thumbnail.width}x${formats.thumbnail.height}px`);
      return getImageUrl(formats.thumbnail.url);
    }
  }
  
  // Fallback to the main URL if no formats available
  console.log('⚠️ Using fallback URL (likely thumbnail):', `${imageData.width}x${imageData.height}px`);
  return imageData.url ? getImageUrl(imageData.url) : null;
};

/**
 * Utility function to get the correct image URL for Strapi images
 * Handles both development (localhost) and production environments
 * @deprecated Use getBestImageUrl for Strapi image objects
 */
export const getImageUrl = (imagePath: string): string => {
  // If the image path already has a full URL, return it as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Resolve API base URL (env var or sensible fallback)
  const envApiUrl = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
  const apiBaseUrl = envApiUrl
    ? envApiUrl
    : ((import.meta as any)?.env?.DEV
        ? 'http://localhost:1337/api'
        : 'https://elegant-advice-80456e5961.strapiapp.com/api');
  
  // Extract the base URL without /api
  const baseUrl = apiBaseUrl.replace('/api', '');
  
  // Return the full image URL
  return `${baseUrl}${imagePath}`;
};

/**
 * Utility function to get the correct API base URL
 */
export const getApiBaseUrl = (): string => {
  const envApiUrl = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
  if (envApiUrl) return envApiUrl;
  if ((import.meta as any)?.env?.DEV) return 'http://localhost:1337/api';
  return 'https://elegant-advice-80456e5961.strapiapp.com/api';
};